import React, { useEffect, useState } from "react";
import { Loader2, AlertTriangle, Package, Truck, CheckCircle2, MailIcon, MapPin, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import api, { readError } from "@/lib/api";
import { formatEurPlain } from "@/lib/money";

const STATUS_TABS = ["all", "pending_payment", "paid", "processing", "shipped", "completed", "cancelled", "refunded"];
const STATUS_COLOR = {
  pending_payment: "bg-muted text-muted-foreground",
  paid: "bg-primary text-primary-foreground",
  processing: "bg-secondary/30 text-secondary",
  shipped: "bg-blue-500/15 text-blue-600",
  completed: "bg-green-600/15 text-green-700",
  cancelled: "bg-destructive/15 text-destructive",
  refunded: "bg-orange-500/15 text-orange-600",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("hr-HR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function AdminOrders() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data: r } = await api.get("/admin/orders", { params });
      setData(r);
    } catch (err) {
      setError(readError(err, "Could not load orders."));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try {
      const { data: r } = await api.patch(`/admin/orders/${id}`, { status });
      toast.success(`Marked as ${status}`);
      setActive(r);
      await load();
    } catch (err) {
      toast.error(readError(err, "Could not update status."));
    }
  };

  return (
    <div className="space-y-7 animate-fade-up">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-primary">Commerce</div>
          <h1 className="mt-1 font-serif text-4xl text-foreground">Orders</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Ready-stock orders paid via Stripe. Track each through: Paid → Processing → Shipped → Completed.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="orders-refresh">
          <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="orders-filter-tabs">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`orders-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.12em] transition-colors ${
              filter === s ? "bg-primary text-primary-foreground shadow-interactive" : "border border-border text-foreground/70 hover:text-primary hover:border-primary/40"
            }`}
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
        </div>
      )}

      {data === null ? (
        <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
        </div>
      ) : data.items.length === 0 ? (
        <Card className="bg-card border-border/60 shadow-ambient">
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            <Package className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" strokeWidth={1.5} />
            No orders here.
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="orders-list">
          {data.items.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                data-testid={`order-row-${o.id}`}
                onClick={() => setActive(o)}
                className="w-full text-left rounded-lg bg-card border border-border/60 hover:border-primary/40 hover:shadow-interactive transition-all p-4 flex items-start gap-3"
              >
                <div className="rounded-lg bg-primary/10 p-2 mt-0.5">
                  <Package className="h-4 w-4 text-primary" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="font-serif text-base text-foreground truncate">
                      {(o.items || []).map((it) => (typeof it.product_name === "string" ? it.product_name : it.product_name?.hr || it.product_slug)).join(", ") || "—"}
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">{fmt(o.created_at)}</div>
                  </div>
                  <div className="mt-0.5 text-sm text-foreground/75 truncate">{o.customer?.name} · {o.customer?.email}</div>
                  <div className="mt-1 flex items-baseline gap-3">
                    <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Total</span>
                    <span className="text-sm text-foreground font-medium">{formatEurPlain(o.payment?.amount_eur || 0)}</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">· {o.payment?.provider || "—"}</span>
                  </div>
                </div>
                <Badge className={STATUS_COLOR[o.status] || ""}>{(o.status || "—").replace(/_/g, " ")}</Badge>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="bg-card border-border max-w-lg w-full overflow-y-auto" data-testid="order-detail-sheet">
          {active && (
            <>
              <SheetHeader>
                <div className="text-[10px] uppercase tracking-[0.2em] text-primary mb-1">{active.locale?.toUpperCase()} · {fmt(active.created_at)}</div>
                <SheetTitle className="font-serif text-2xl">Order {active.id.slice(0, 8).toUpperCase()}</SheetTitle>
                <SheetDescription>
                  <div className="space-y-1 text-sm">
                    <div className="text-foreground">{active.customer?.name}</div>
                    <a href={`mailto:${active.customer?.email}`} className="text-primary hover:underline flex items-center gap-1">
                      <MailIcon className="h-3 w-3" /> {active.customer?.email}
                    </a>
                    {active.shipping_address && (
                      <div className="flex items-start gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 mt-0.5 shrink-0" strokeWidth={1.6} />
                        <span>{active.shipping_address.street}, {active.shipping_address.postal} {active.shipping_address.city}, {active.shipping_address.country}</span>
                      </div>
                    )}
                  </div>
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Items</div>
                  <ul className="space-y-3" data-testid="order-items">
                    {(active.items || []).map((it, i) => (
                      <li key={i} className="rounded-lg border border-border/60 p-3 bg-background">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="font-serif text-base text-foreground">{typeof it.product_name === "string" ? it.product_name : (it.product_name?.hr || it.product_slug)}</div>
                          <div className="text-sm text-foreground">{formatEurPlain(it.unit_price_eur)}</div>
                        </div>
                        {(it.configuration || []).length > 0 && (
                          <ul className="mt-2 text-xs text-muted-foreground space-y-0.5">
                            {it.configuration.map((c) => (
                              <li key={c.field_id}>{c.field_label}: <span className="text-foreground/80">{c.value_label}</span> {c.price_delta_eur > 0 && <span>(+{formatEurPlain(c.price_delta_eur)})</span>}</li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg bg-muted/40 border border-border/60 p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Payment provider</span><span className="text-foreground">{active.payment?.provider}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Payment status</span><span className="text-foreground">{active.payment?.status}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Stripe session</span><code className="text-[10px] text-foreground/80 break-all">{active.payment?.session_id}</code></div>
                  <div className="flex items-baseline justify-between border-t border-border/60 pt-2 mt-2">
                    <span className="font-medium text-foreground">Total</span>
                    <span className="font-serif text-2xl text-primary">{formatEurPlain(active.payment?.amount_eur)}</span>
                  </div>
                </div>
              </div>

              <SheetFooter className="mt-7 gap-2 flex-wrap flex-row">
                {active.status === "paid" && (
                  <Button size="sm" onClick={() => setStatus(active.id, "processing")} data-testid="order-mark-processing">
                    <Truck className="h-3.5 w-3.5" strokeWidth={1.5} /> Mark processing
                  </Button>
                )}
                {active.status === "processing" && (
                  <Button size="sm" onClick={() => setStatus(active.id, "shipped")} data-testid="order-mark-shipped">
                    <Truck className="h-3.5 w-3.5" strokeWidth={1.5} /> Mark shipped
                  </Button>
                )}
                {active.status === "shipped" && (
                  <Button size="sm" onClick={() => setStatus(active.id, "completed")} data-testid="order-mark-completed" className="bg-green-600 hover:bg-green-700 text-white">
                    <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Complete
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setStatus(active.id, "cancelled")} data-testid="order-mark-cancelled" className="text-destructive hover:text-destructive">Cancel</Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
